import React from 'react';
import Icon from '../../../components/AppIcon';

const PreviousDecisionsPanel = ({
  previousDecisions = [],
  onApplyDecision = () => {},
  className = ''
}) => {
  if (previousDecisions?.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="text-center text-muted-foreground">
          <Icon name="History" size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No previous decisions found</p>
        </div>
      </div>
    );
  }

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'merged': return 'Merge';
      case 'separate': return 'Split';
      case 'skipped': return 'Clock';
      default: return 'Help';
    }
  };

  const getDecisionColor = (decision) => {
    switch (decision) {
      case 'merged': return 'text-success';
      case 'separate': return 'text-muted-foreground';
      case 'skipped': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getDecisionBackground = (decision) => {
    switch (decision) {
      case 'merged': return 'bg-success bg-opacity-10 border-success border-opacity-20';
      case 'separate': return 'bg-muted bg-opacity-50 border-border';
      case 'skipped': return 'bg-warning bg-opacity-10 border-warning border-opacity-20';
      default: return 'bg-muted bg-opacity-50 border-border';
    }
  };

  const formatDecisionText = (decision) => {
    switch (decision) {
      case 'merged': return 'Merged';
      case 'separate': return 'Kept Separate';
      case 'skipped': return 'Skipped';
      default: return 'Unknown';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-foreground">
          Previous Decisions
        </h3>
        <span className="text-xs text-muted-foreground">
          {previousDecisions?.length} similar cases
        </span>
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {previousDecisions?.map((decision, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border transition-colors duration-150 ${getDecisionBackground(decision?.decision)}`}
          >
            {/* Decision Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getDecisionIcon(decision?.decision)} 
                  size={14} 
                  className={getDecisionColor(decision?.decision)}
                />
                <span className={`text-sm font-medium ${getDecisionColor(decision?.decision)}`}>
                  {formatDecisionText(decision?.decision)}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {Math.round(decision?.similarity * 100)}% similar
              </span>
            </div>

            {/* Product Comparison */}
            <div className="space-y-1 mb-3">
              <div className="text-xs text-foreground">
                <span className="font-medium">Product A:</span> {decision?.productA}
              </div>
              <div className="text-xs text-foreground">
                <span className="font-medium">Product B:</span> {decision?.productB}
              </div>
            </div>

            {/* Matching Criteria */}
            {decision?.matchingCriteria && decision?.matchingCriteria?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {decision?.matchingCriteria?.map((criteria, criteriaIndex) => (
                  <span
                    key={criteriaIndex}
                    className="text-xs px-1.5 py-0.5 bg-primary bg-opacity-10 text-primary rounded"
                  >
                    {criteria}
                  </span>
                ))}
              </div>
            )}

            {/* Decision Metadata */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {new Date(decision.timestamp)?.toLocaleDateString()}
              </span>
              {decision?.confidence && (
                <span>
                  {Math.round(decision?.confidence * 100)}% confidence
                </span>
              )}
            </div>

            {/* Apply Decision Button */}
            {decision?.decision !== 'skipped' && (
              <div className="mt-3 pt-2 border-t border-border border-opacity-50">
                <button
                  onClick={() => onApplyDecision(decision)}
                  className="w-full text-xs text-primary hover:text-primary-foreground hover:bg-primary rounded py-1.5 transition-colors duration-150"
                >
                  Apply Same Decision
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Learning Indicator */}
      <div className="pt-3 border-t border-border mt-3">
        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Brain" size={12} />
          <span>
            AI learns from your decisions to improve future matching
          </span>
        </div>
      </div>
    </div>
  );
};

export default PreviousDecisionsPanel;